<template>
  <div v-if="!hasPermission('CanViewReturns')" class="container view-claims-component vcc">
    <b-row>
      <b-col>
        <not-authorized />
      </b-col>
    </b-row>
  </div>
  <list-layout v-else>
    <template #leftSidebar>
      <ReverseLogisticsMenu />
      <br />
      <ClaimsOrdersSearch :search-by-number-label="getContentItem('searchByMessageNumber')" />
    </template>

    <template #heading>
      <b-row class="vcc__heading">
        <b-col>
          <h1>{{ getContentItem("claimsLabel") }}</h1>
        </b-col>
        <b-col sm="auto">
          <router-link
            v-if="hasPermission('CanSubmitDiscrepancyClaims')"
            id="startNewClaimButton"
            to="/ClaimsAndReturns/CreateClaim"
            class="pull-right btn btn-large dn-btn-large"
          >
            {{ getContentItem("startNewClaim") }}
          </router-link>
        </b-col>
      </b-row>
    </template>

    <template #subTitle>
      {{ getContentItem("claimsIndexSubTitle") }}
    </template>

    <template #filters>
      <b-row class="modify my-3 mx-0 align-items-center vcc__dealer-selector">
        <b-col md="10">
          <AvailableDealerCodeSelector

          :show-all-branches-option="true"
            class="vSelectDropDownText-disableBold"
            @selectDealerCode="setUserDealerCode"
          />
        </b-col>
        <b-col md="2">
          <DnFunctionButton
            id="updateClaimQuery"
            :callback-fn="loadClaims"
            :text="getContentItem('updateLabel')"
            :size="largeSize"
          />
        </b-col>
      </b-row>
      <b-row v-if="userHasAccessToAllBranches || isAllBranchesSelected">
        <b-col>
          <div v-if="userHasAccessToAllBranches" class="alert alert-secondary">
            {{ getContentItem("ClaimsStarAccessMessage") }}
          </div>
          <div v-else-if="isAllBranchesSelected" class="alert alert-secondary">
            {{ getContentItem("ClaimsAllBranchesMessage") }}
            <div v-if="totalDealersLoaded !== 0 && totalDealersLoaded !== accountIds.length">
              <loading-spinner /> {{ `Claims for ${totalDealersLoaded} out of ${accountIds.length} dealers have been loaded.` }}
            </div>
          </div>
        </b-col>
      </b-row>
      <b-row class="align-items-center vcc__narrow-results">
        <b-col sm="auto" class="vcc__narrow-results-item" md="3">
          <b-form inline>
            <label>{{ getContentItem("perPage") }}&nbsp;</label>
            <b-form-select
              id="claimsPerPage"
              v-model="pagination.perPage"
              size="sm"
              :options="pagination.perPageList"
              class="vSelectDropDownText"
            />
          </b-form>
        </b-col>
        <b-col class="vcc__narrow-results-item" md="3">
          <date-range-select :selected.sync="selectedClaimsDateRange" />
        </b-col>
        <b-col class="vcc__narrow-results-item" md="3">
          <v-select
            id="claimsStatus"
            v-model="selectedStatus"
            class="vSelectDropDownText disableClear"
            :options="claimsStatuses"
          />
        </b-col>
        <b-col class="vcc__narrow-results-item" md="3">
          <b-form-input v-model="filter" size="sm" :placeholder="getContentItem('search')" />
        </b-col>
      </b-row>
    </template>

    <template #table>
      <b-row class="mt-3">
        <b-col>
          <DnTable
            id="ClaimsTable"
            hover
            striped
            :items="claimsData"
            :fields="claimsDataColumns"
            :current-page="pagination.currentPage"
            :per-page="pagination.perPage"
            :showPagination="true"
            :filter="filter + selectedStatus.value + selectedClaimsDateRange.value"
            :filter-function="filterGrid"
            stacked="md"
            :no-provider-filtering="true"
            :no-provider-paging="true"
            :no-local-sorting="false"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            class="blue"
            show-empty
            empty-text="No Claims Found"
            :busy="isLoadingClaims"
            @filtered="paginationReset"
            @sort-changed="paginationReset"
            @update:currentPage="updatePage"
          >
            <template #table-busy>
              <loading-spinner class="mx-auto" />
            </template>
            <template #CustomerReferenceNumber="{ row }">
              <router-link :to="'/claimsAndReturns/claimDetails?claimNumber=' + row.item.ClaimNumber + '&accountId=' + row.item.AccountId">
                {{ row.item.CustomerReferenceNumber }}
              </router-link>
            </template>
            <template #ClaimEnteredDate="{ row }">
              <date-time :date-string="row.item.ClaimEnteredDate" />
            </template>
          </DnTable>
        </b-col>
      </b-row>
      <b-row v-if="pagination.totalRows > 0" class="align-items-center">
        <b-col id="paginationDisplay" class="text-center">
          {{
            format(
              getContentItem("showingPagination"),
              paginationFirstRecord,
              paginationLastRecord,
              pagination.totalRows
            )
          }}
        </b-col>
      </b-row>
    </template>
  </list-layout>
</template>

<script>
import vSelect from "vue-select";
import { mapGetters } from "vuex";
import AvailableDealerCodeSelector from "@/components/Dropdowns/AvailableDealerCodeSelector";
import { paginationMixin } from "@/components/mixins/paginationMixin";
import ClaimsOrdersSearch from "@/components/SubMenus/ClaimsOrdersSearch";
import ReverseLogisticsMenu from "@/components/SubMenus/ReverseLogisticsMenu";
import { ClaimsService } from "@/services/ClaimsService";
import { stringHelper } from "@/components/mixins/stringHelper";
import DateTime from "@/components/Elements/DateTime";
import { sizeHelper } from "@/components/mixins/sizeHelper";
import DnFunctionButton from "@/components/Elements/DnFunctionButton";
import DateRangeSelect from "@/components/Elements/DateRangeSelect";
import ListLayout from "@/components/layouts/listLayout";
import NotAuthorized from "@/components/Elements/NotAuthorized";
import { LoadingSpinner } from "@/components";
import DnTable from "@/components/Elements/DnTable/DnTable.vue";

export default {
  components: {
    vSelect,
    ListLayout,
    AvailableDealerCodeSelector,
    ClaimsOrdersSearch,
    ReverseLogisticsMenu,
    DateTime,
    DnFunctionButton,
    DateRangeSelect,
    NotAuthorized,
    LoadingSpinner,
    DnTable,
  },
  mixins: [paginationMixin, stringHelper, sizeHelper],
  data: () => ({
    filter: null,
    selectedClaimsDateRange: { label: "All Dates", value: 0 },
    claimsStatuses: [
      { label: "All Statuses", value: "All" },
      { label: "New", value: "New" },
      { label: "Pending", value: "Pending" },
      { label: "Closed", value: "Closed" },
      { label: "Denied", value: "Denied" },
      { label: "Cancelled", value: "Cancelled" },
    ],
    selectedStatus: { label: "All Statuses", value: "All" },
    claimsDataColumns: [
      { key: "CustomerReferenceNumber", label: "Dealer Claim Reference", sortable: true },
      { key: "ClaimNumber", label: "Claim Number", sortable: true },
      { key: "ClaimEnteredDate", label: "Date Created", sortable: true },
      { key: "ClaimStatus", label: "Status", sortable: true },
    ],
    claimsData: [],
    sortBy: "ClaimEnteredDate",
    sortDesc: true,
    selectedAccountId: 0,
    isLoadingClaims: false,
    accountIds: [],
    isAllBranchesSelected: false,
    userHasAccessToAllBranches: false,
    totalDealersLoaded: 0,
  }),
  computed: {
    ...mapGetters(["getContentItem", "availableDealerCodes", "user", "activeAccountId", "hasPermission"]),
  },
  mounted() {
    this.selectedAccountId = this.activeAccountId;
    this.accountIds = [this.selectedAccountId];
    this.loadClaims();
  },

  methods: {
    setUserDealerCode: function (selectedAvailableDealerCode) {
      this.userHasAccessToAllBranches = false;
      if (selectedAvailableDealerCode?.value === "All Branches") {
        this.selectedAccountId = undefined;
        this.isAllBranchesSelected = true;
        if (this.user.DealerRange === "*") {
          // a "*" user has access to every account in the system, so fanning out per-account isn't feasible
          this.userHasAccessToAllBranches = true;
          this.accountIds = [];
        } else {
          this.accountIds = this.availableDealerCodes.map((dealerCode) => dealerCode.AccountId);
        }
      } else {
        this.selectedAccountId = selectedAvailableDealerCode.AccountId;
        this.isAllBranchesSelected = false;
        this.accountIds = [this.selectedAccountId];
      }
    },
    loadClaims: function () {
      if (this.selectedAccountId == undefined && !this.isAllBranchesSelected) {
        // account wasn't selected and update was clicked (so no change)
        return;
      }
      this.claimsData = [];
      this.totalDealersLoaded = 0;
      if (this.userHasAccessToAllBranches || this.accountIds.length === 0) {
        this.isLoadingClaims = false;
        return;
      }
      this.isLoadingClaims = true;
      const concurrency = 5;
      const queue = [...this.accountIds];
      const fetchNext = () => {
        const accountId = queue.shift();
        if (accountId === undefined) {
          return;
        }
        ClaimsService.GetClaimSummaries(accountId, this.user.UserId)
          .then((claimHeaders) => {
            // rows don't carry an account, so tag them here for per-row links in All Branches mode
            this.claimsData.push(...claimHeaders.data.map((claim) => ({ ...claim, AccountId: accountId })));
          })
          .finally(() => {
            this.totalDealersLoaded++;
            if (this.totalDealersLoaded === this.accountIds.length) {
              this.isLoadingClaims = false;
            }
            fetchNext();
          });
      };
      // limit in-flight requests so "All Branches" doesn't exhaust the browser's connection pool
      for (let i = 0; i < Math.min(concurrency, this.accountIds.length); i++) {
        fetchNext();
      }
    },
    filterGrid(val) {
      if (!this.filter && !this.selectedClaimsDateRange && !this.selectedStatus) {
        return true;
      }
      var matchesSearchFilter = true;
      var matchesDateFilter = true;
      var matchesStatusFilter = true;

      if (this.filter) {
        const filterUpper = this.filter.toUpperCase();
        matchesSearchFilter =
          val.CustomerReferenceNumber?.toUpperCase().includes(filterUpper) ||
          val.ClaimNumber?.toUpperCase().includes(filterUpper);
      }

      if (this.selectedClaimsDateRange.value != 0) {
        var start = new Date();
        var claimDate = new Date(val.ClaimEnteredDate);
        // difference below is in epoch so need to convert to days
        var difference = (start - claimDate) / (1000 * 60 * 60 * 24);
        matchesDateFilter = difference <= this.selectedClaimsDateRange.value;
      }

      if (this.selectedStatus.value != "All") {
        matchesStatusFilter = val.ClaimStatus == this.selectedStatus.value;
      }

      return matchesSearchFilter && matchesDateFilter && matchesStatusFilter;
    },
       updatePage(newPage) {
        this.pagination.currentPage = newPage;
    },
  },
};
</script>

<style lang="scss" src="./ViewClaims.scss"></style>
